import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import Modal from "react-responsive-modal";
import Button from "../../../../../common/button/Button";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import WComponent from "../../../../../common/WComponent";
import { CANONICAL } from "../../shared/helpers/constants";
import "../../afs-walkthrough.css";

function fmt(val) {
	if (val == null) return "—";
	let abs = Math.abs(val).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
	return val < 0 ? "($" + abs + ")" : "$" + abs;
}

function fmtDate(d) {
	if (!d) return "—";
	try {
		let parts = String(d).split("T")[0].split("-");
		let months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		return months[parseInt(parts[1], 10) - 1] + " " + parseInt(parts[2], 10) + ", " + parts[0];
	} catch (e) { return String(d); }
}

// CCA class catalog is loaded from the API (source of truth: workflow-api/services/fixedAssetRegisterService.js).
// A minimal fallback is used only if the API hasn't responded yet.
const FALLBACK_CCA_CATALOG = [
	{ canonical_code: CANONICAL.COMPUTER_EQUIPMENT, display_name: "Computer Equipment", cca_class: 50, cca_rate_pct: 55 },
];

class FixedAssetRegister extends WComponent {
	state = {
		assets: [],
		totals: { cost: 0, accumulatedDepreciation: 0, nbv: 0 },
		ccaCatalog: FALLBACK_CCA_CATALOG,
		loading: true,
		showAddForm: false,
		editingId: null,
		calcModalAsset: null,
		disposeModalAsset: null,
		disposeForm: { disposalDate: "", disposalProceeds: "0" },
		deleteModalAsset: null,
		form: {
			assetName: "", acquisitionDate: "", cost: "",
			canonicalCode: FALLBACK_CCA_CATALOG[0].canonical_code,
			depreciationRate: String(FALLBACK_CCA_CATALOG[0].cca_rate_pct),
			accumulatedDepreciation: "0",
		},
	};

	componentDidMount() {
		this.load();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.clientId !== this.props.clientId) {
			this.load();
		}
	}

	handleCategoryChange = (newCode) => {
		let { form, ccaCatalog } = this.state;
		let category = ccaCatalog.find(function (c) { return c.canonical_code === newCode; });
		this.update({
			form: {
				...form,
				canonicalCode: newCode,
				// Auto-set the rate to the CCA rate for this class
				depreciationRate: category ? String(category.cca_rate_pct) : form.depreciationRate,
			},
		});
	};

	load = async () => {
		this.update({ loading: true });
		let result = await BookkeepingService.getFixedAssets({ clientId: this.props.clientId });
		if (result) {
			let next = {
				assets: result.assets || [],
				totals: result.totals || { cost: 0, accumulatedDepreciation: 0, nbv: 0 },
			};
			if (result.cca_catalog && result.cca_catalog.length > 0) next.ccaCatalog = result.cca_catalog;
			this.update(next);
		}
		this.update({ loading: false });
	};

	resetForm = () => {
		let { ccaCatalog } = this.state;
		this.update({
			form: {
				assetName: "", acquisitionDate: "", cost: "",
				canonicalCode: ccaCatalog[0].canonical_code,
				depreciationRate: String(ccaCatalog[0].cca_rate_pct),
				accumulatedDepreciation: "0",
			},
			editingId: null,
			showAddForm: false,
		});
	};

	handleSubmit = async () => {
		let { form, editingId } = this.state;
		if (!form.assetName || !form.cost) return;
		if (editingId) {
			await BookkeepingService.updateFixedAsset({ clientId: this.props.clientId, assetId: editingId, updates: form });
		} else {
			await BookkeepingService.addFixedAsset({ clientId: this.props.clientId, asset: form });
		}
		await this.load();
		this.resetForm();
		if (this.props.onChange) this.props.onChange();
	};

	startEdit = (asset) => {
		let { ccaCatalog } = this.state;
		this.update({
			form: {
				assetName: asset.assetName,
				acquisitionDate: asset.acquisitionDate || "",
				cost: asset.cost,
				canonicalCode: asset.canonicalCode || (ccaCatalog[0] && ccaCatalog[0].canonical_code),
				depreciationRate: asset.depreciationRate || 20,
				accumulatedDepreciation: asset.accumulatedDepreciation || 0,
			},
			editingId: asset.id,
			showAddForm: true,
		});
	};

	handleDelete = (asset) => {
		this.update({ deleteModalAsset: asset });
	};

	confirmDelete = async () => {
		let { deleteModalAsset } = this.state;
		if (!deleteModalAsset) return;
		await BookkeepingService.deleteFixedAsset({ clientId: this.props.clientId, assetId: deleteModalAsset.id });
		this.update({ deleteModalAsset: null });
		await this.load();
		if (this.props.onChange) this.props.onChange();
	};

	handleMarkDisposed = (asset) => {
		this.update({
			disposeForm: {
				disposalDate: new Date().toISOString().split("T")[0],
				disposalProceeds: "0",
			},
			disposeModalAsset: asset,
		});
	};

	confirmDispose = async () => {
		let { disposeModalAsset, disposeForm } = this.state;
		if (!disposeModalAsset || !disposeForm.disposalDate) return;
		await BookkeepingService.updateFixedAsset({
			clientId: this.props.clientId,
			assetId: disposeModalAsset.id,
			updates: { disposalDate: disposeForm.disposalDate, disposalProceeds: parseFloat(disposeForm.disposalProceeds) || 0 },
		});
		this.update({ disposeModalAsset: null });
		await this.load();
		if (this.props.onChange) this.props.onChange();
	};

	render() {
		let { assets, totals, ccaCatalog, loading, showAddForm, editingId, calcModalAsset, disposeModalAsset, disposeForm, deleteModalAsset, form } = this.state;

		let activeAssets = assets.filter(function (a) { return !a.disposalDate; });
		let disposedAssets = assets.filter(function (a) { return a.disposalDate; });

		// Group active assets by canonical_code (or category)
		let groupedAssets = {};
		for (let a of activeAssets) {
			let key = a.canonicalCode || "OTHER";
			if (!groupedAssets[key]) {
				groupedAssets[key] = {
					canonicalCode: key,
					categoryName: a.assetClassLabel || (ccaCatalog.find(function (c) { return c.canonical_code === key; }) || { display_name: "Other" }).display_name,
					rateLabel: a.depreciationRate || "",
					method: a.method || null,
					assets: [],
					groupTotals: { cost: 0, accDep: 0, nbv: 0 },
				};
			}
			let cost = parseFloat(a.cost) || 0;
			let accDep = parseFloat(a.accumulatedDepreciation) || 0;
			groupedAssets[key].assets.push(a);
			groupedAssets[key].groupTotals.cost += cost;
			groupedAssets[key].groupTotals.accDep += accDep;
			groupedAssets[key].groupTotals.nbv += (cost - accDep);
		}
		let groupList = Object.values(groupedAssets);

		return (
			<div className="afs-far">
				<div className="afs-far__header">
					<span className="afs-far__title">Fixed Asset Register</span>
					<button className="afs-far__add-btn" onClick={() => this.update({ showAddForm: !showAddForm, editingId: null })}>
						<Icon.Plus size={13} /> Add Asset
					</button>
				</div>

				<Modal open={showAddForm} center onClose={this.resetForm}>
					<div className="modal-title">{editingId ? "Edit Asset" : "Add Asset"}</div>
					<div className="modal-content">
						<div className="afs-far__form-grid">
							<div className="afs-far__field afs-far__field--span2">
								<label className="afs-far__label">Asset Name</label>
								<input className="afs-far__input" placeholder="e.g., Dell Server" value={form.assetName} onChange={(e) => this.update({ form: { ...form, assetName: e.target.value } })} autoFocus />
							</div>
							<div className="afs-far__field">
								<label className="afs-far__label">Category</label>
								<select className="afs-far__input" value={form.canonicalCode} onChange={(e) => this.handleCategoryChange(e.target.value)}>
									{ccaCatalog.map(function (c) {
										let label = c.cca_class ? c.display_name + " — Class " + c.cca_class + " (" + c.cca_rate_pct + "%)" : c.display_name;
										return <option key={c.canonical_code} value={c.canonical_code}>{label}</option>;
									})}
								</select>
							</div>
							<div className="afs-far__field">
								<label className="afs-far__label">Acquisition Date</label>
								<input className="afs-far__input" type="date" value={form.acquisitionDate} onChange={(e) => this.update({ form: { ...form, acquisitionDate: e.target.value } })} />
							</div>
							<div className="afs-far__field">
								<label className="afs-far__label">Cost</label>
								<div className="afs-far__input-wrap">
									<span className="afs-far__input-prefix">$</span>
									<input className="afs-far__input afs-far__input--mono afs-far__input--prefixed" type="number" placeholder="0.00" value={form.cost} onChange={(e) => this.update({ form: { ...form, cost: e.target.value } })} />
								</div>
							</div>
							<div className="afs-far__field">
								<label className="afs-far__label">CCA Rate</label>
								<div className="afs-far__input-wrap">
									<input className="afs-far__input afs-far__input--mono afs-far__input--suffixed" type="number" placeholder="20" value={form.depreciationRate} onChange={(e) => this.update({ form: { ...form, depreciationRate: e.target.value } })} />
									<span className="afs-far__input-suffix">%</span>
								</div>
								<div className="afs-far__field-hint">
									Auto-set from category. Half-year rule (× ½) applies in year of acquisition.
								</div>
							</div>
							<div className="afs-far__field">
								<label className="afs-far__label">Accumulated Depreciation (prior year)</label>
								<div className="afs-far__input-wrap">
									<span className="afs-far__input-prefix">$</span>
									<input className="afs-far__input afs-far__input--mono afs-far__input--prefixed" type="number" placeholder="0.00" value={form.accumulatedDepreciation} onChange={(e) => this.update({ form: { ...form, accumulatedDepreciation: e.target.value } })} />
								</div>
								<div className="afs-far__field-hint">
									Leave $0 for newly-acquired assets. Enter prior-year accumulated for assets acquired before this fiscal year.
								</div>
							</div>
						</div>
					</div>
					<div className="modal-footer">
						<Button view="secondary" text="Cancel" onClick={this.resetForm} />
						<Button text={editingId ? "Update Asset" : "Add Asset"} onClick={this.handleSubmit} disabled={!form.assetName || !form.cost} />
					</div>
				</Modal>

				{loading ? (
					<div className="afs-far__loading">Loading...</div>
				) : assets.length === 0 ? (
					<div className="afs-far__empty">No fixed assets recorded. Click "Add Asset" to start the register.</div>
				) : (
					<>
						{groupList.map((group) => {
							return (
								<div key={group.canonicalCode} className="afs-far__group">
									<div className="afs-far__group-header">
										<span className="afs-far__group-title">{group.categoryName}</span>
										{group.rateLabel && (
											<span className="afs-far__group-rate">{group.rateLabel}</span>
										)}
									</div>
									<table className="afs-far__table">
										<thead>
											<tr>
												<th className="afs-far__th">Asset</th>
												<th className="afs-far__th">Acquired</th>
												<th className="afs-far__th afs-far__th--right">Cost</th>
												<th className="afs-far__th afs-far__th--right">Acc Dep</th>
												<th className="afs-far__th afs-far__th--right">NBV</th>
												<th className="afs-far__th afs-far__col-actions"></th>
											</tr>
										</thead>
										<tbody>
											{group.assets.map((a) => {
												let cost = parseFloat(a.cost) || 0;
												let accDep = parseFloat(a.accumulatedDepreciation) || 0;
												let nbv = cost - accDep;
												let hasCalc = !!a.calculationNote;
												return (
													<tr key={a.id} className="afs-far__row">
														<td className="afs-far__td afs-far__td--name">{a.assetName}</td>
														<td className="afs-far__td">{fmtDate(a.acquisitionDate)}</td>
														<td className="afs-far__td afs-far__td--right afs-far__td--mono">{fmt(cost)}</td>
														<td
															className={clsx("afs-far__td", "afs-far__td--right", "afs-far__td--mono", hasCalc && "afs-far__td--calc-link")}
															onClick={hasCalc ? () => this.update({ calcModalAsset: Object.assign({}, a, { groupRateLabel: group.rateLabel, groupCategoryName: group.categoryName }) }) : undefined}
															title={hasCalc ? "Click to see calculation" : ""}
														>
															{fmt(accDep)}
														</td>
														<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(nbv)}</td>
														<td className="afs-far__td afs-far__td--actions">
															<button className="afs-far__action-btn" title="Edit" onClick={() => this.startEdit(a)}><Icon.Edit3 size={12} /></button>
															<button className="afs-far__action-btn" title="Mark disposed" onClick={() => this.handleMarkDisposed(a)}><Icon.Archive size={12} /></button>
															<button className="afs-far__action-btn" title="Delete" onClick={() => this.handleDelete(a)}><Icon.Trash2 size={12} /></button>
														</td>
													</tr>
												);
											})}
											<tr className="afs-far__subtotal-row">
												<td className="afs-far__td afs-far__td--bold" colSpan={2}>Subtotal</td>
												<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(group.groupTotals.cost)}</td>
												<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(group.groupTotals.accDep)}</td>
												<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(group.groupTotals.nbv)}</td>
												<td className="afs-far__td" />
											</tr>
										</tbody>
									</table>
								</div>
							);
						})}

						{groupList.length > 1 && (
							<div className="afs-far__grand-total">
								<table className="afs-far__table">
									<tbody>
										<tr className="afs-far__total-row">
											<td className="afs-far__td afs-far__td--bold" colSpan={2}>TOTAL ALL ASSETS</td>
											<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(totals.cost)}</td>
											<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(totals.accumulatedDepreciation)}</td>
											<td className="afs-far__td afs-far__td--right afs-far__td--mono afs-far__td--bold">{fmt(totals.nbv)}</td>
											<td className="afs-far__td afs-far__col-actions" />
										</tr>
									</tbody>
								</table>
							</div>
						)}

						{disposedAssets.length > 0 && (
							<div className="afs-far__disposed-section">
								<div className="afs-far__disposed-title">Disposed During Year</div>
								<table className="afs-far__table">
									<tbody>
										{disposedAssets.map((a) => {
											let cost = parseFloat(a.cost) || 0;
											let proceeds = parseFloat(a.disposalProceeds) || 0;
											let accDep = parseFloat(a.accumulatedDepreciation) || 0;
											let gainLoss = proceeds - (cost - accDep);
											return (
												<tr key={a.id} className="afs-far__row afs-far__row--disposed">
													<td className="afs-far__td afs-far__td--name">{a.assetName}</td>
													<td className="afs-far__td">Disposed {fmtDate(a.disposalDate)}</td>
													<td className="afs-far__td afs-far__td--right afs-far__td--mono">Cost: {fmt(cost)}</td>
													<td className="afs-far__td afs-far__td--right afs-far__td--mono">Proceeds: {fmt(proceeds)}</td>
													<td className={clsx("afs-far__td", "afs-far__td--right", "afs-far__td--mono", gainLoss >= 0 ? "afs-far__td--gain" : "afs-far__td--loss")}>
														{gainLoss >= 0 ? "Gain" : "Loss"}: {fmt(gainLoss)}
													</td>
													<td className="afs-far__td afs-far__td--actions">
														<button className="afs-far__action-btn" onClick={() => this.handleDelete(a)}><Icon.Trash2 size={12} /></button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}

				<Modal open={!!calcModalAsset} center onClose={() => this.update({ calcModalAsset: null })}>
					{calcModalAsset && (
						<>
							<div className="modal-title">Depreciation Calculation</div>
							<div className="modal-content">
								<div className="afs-far__modal-block">
									<div className="afs-far__modal-asset-name afs-far__modal-asset-name--gap">{calcModalAsset.assetName}</div>
									<div className="afs-far__modal-asset-meta">Acquired {fmtDate(calcModalAsset.acquisitionDate)} — {calcModalAsset.groupCategoryName}</div>
								</div>

								<table className="afs-far__calc-table">
									<tbody>
										<tr>
											<td className="afs-far__calc-label">Cost</td>
											<td className="afs-far__calc-val">{fmt(parseFloat(calcModalAsset.cost) || 0)}</td>
										</tr>
										<tr>
											<td className="afs-far__calc-label">Method</td>
											<td className="afs-far__calc-val--plain">{calcModalAsset.groupRateLabel || "—"}</td>
										</tr>
										{calcModalAsset.method === "cca_half_year" && calcModalAsset.ccaRate && (
											<>
												<tr>
													<td className="afs-far__calc-label">Full CCA rate</td>
													<td className="afs-far__calc-val">{(calcModalAsset.ccaRate * 100).toFixed(0)}%</td>
												</tr>
												<tr>
													<td className="afs-far__calc-label">Half-year rule (year of acquisition)</td>
													<td className="afs-far__calc-val">× ½</td>
												</tr>
												<tr className="afs-far__calc-row--rule">
													<td className="afs-far__calc-label--strong">Effective rate</td>
													<td className="afs-far__calc-val--strong">{(calcModalAsset.ccaRate * 50).toFixed(1).replace(/\.0$/, "")}%</td>
												</tr>
											</>
										)}
										<tr className="afs-far__calc-row--total">
											<td className="afs-far__calc-total-label">Current-year depreciation</td>
											<td className="afs-far__calc-total-val">{fmt(parseFloat(calcModalAsset.accumulatedDepreciation) || 0)}</td>
										</tr>
									</tbody>
								</table>

								<div className="afs-far__calc-note">
									<div className="afs-far__calc-note-title">Calculation</div>
									<code className="afs-far__calc-code">{calcModalAsset.calculationNote}</code>
									{calcModalAsset.method === "cca_half_year" && (
										<div className="afs-far__calc-note-extra">
											Note: CCA uses the half-year rule for first-year acquisitions. The acquisition month is not used — the half-year rule replaces month-based pro-ration.
										</div>
									)}
								</div>
							</div>
							<div className="modal-footer">
								<Button text="Close" onClick={() => this.update({ calcModalAsset: null })} />
							</div>
						</>
					)}
				</Modal>

				{/* Dispose Asset Modal */}
				<Modal open={!!disposeModalAsset} center onClose={() => this.update({ disposeModalAsset: null })}>
					{disposeModalAsset && (
						<>
							<div className="modal-title">Mark Asset as Disposed</div>
							<div className="modal-content">
								<div className="afs-far__modal-block">
									<div className="afs-far__modal-asset-name">{disposeModalAsset.assetName}</div>
									<div className="afs-far__modal-asset-meta">
										Cost: {fmt(parseFloat(disposeModalAsset.cost) || 0)} · Acc Dep: {fmt(parseFloat(disposeModalAsset.accumulatedDepreciation) || 0)} · NBV: {fmt((parseFloat(disposeModalAsset.cost) || 0) - (parseFloat(disposeModalAsset.accumulatedDepreciation) || 0))}
									</div>
								</div>
								<div className="afs-far__form-grid">
									<div className="afs-far__field">
										<label className="afs-far__label">Disposal Date</label>
										<input
											className="afs-far__input"
											type="date"
											value={disposeForm.disposalDate}
											onChange={(e) => this.update({ disposeForm: { ...disposeForm, disposalDate: e.target.value } })}
											autoFocus
										/>
									</div>
									<div className="afs-far__field">
										<label className="afs-far__label">Disposal Proceeds</label>
										<div className="afs-far__input-wrap">
											<span className="afs-far__input-prefix">$</span>
											<input
												className="afs-far__input afs-far__input--mono afs-far__input--prefixed"
												type="number"
												placeholder="0.00"
												value={disposeForm.disposalProceeds}
												onChange={(e) => this.update({ disposeForm: { ...disposeForm, disposalProceeds: e.target.value } })}
											/>
										</div>
									</div>
								</div>
								<div className="afs-far__dispose-note">
									Gain/loss is computed automatically: proceeds − NBV at disposal date.
								</div>
							</div>
							<div className="modal-footer">
								<Button view="secondary" text="Cancel" onClick={() => this.update({ disposeModalAsset: null })} />
								<Button text="Mark Disposed" onClick={this.confirmDispose} disabled={!disposeForm.disposalDate} />
							</div>
						</>
					)}
				</Modal>

				{/* Delete Asset Modal */}
				<Modal open={!!deleteModalAsset} center onClose={() => this.update({ deleteModalAsset: null })}>
					{deleteModalAsset && (
						<>
							<div className="modal-title">Remove Asset?</div>
							<div className="modal-content">
								Remove <strong>{deleteModalAsset.assetName}</strong> from the Fixed Asset Register? This action cannot be undone.
								{deleteModalAsset.source === "auto_detected" && (
									<div className="afs-far__delete-note">
										This asset was auto-detected from a GL transaction. Removing it from the FAR doesn't delete the underlying transaction — but the asset will reappear next time the FAR is rebuilt unless the transaction is reclassified.
									</div>
								)}
							</div>
							<div className="modal-footer">
								<Button view="secondary" text="Cancel" onClick={() => this.update({ deleteModalAsset: null })} />
								<Button text="Remove" onClick={this.confirmDelete} />
							</div>
						</>
					)}
				</Modal>
			</div>
		);
	}
}

export default FixedAssetRegister;
